import { type SyntheticEvent, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUISpeedProfilesUpdateSpeedProfile } from '../../api/speed-profiles/speed-profiles';

type EditSpeedProfileUsocsFormProps = {
  speedProfile: string;
  currentUsocs: string[];
  onSuccess: () => void;
  onClose: () => void;
};

const USOC_PATTERN = /^[a-zA-Z0-9]+$/;
const MAX_USOC_LENGTH = 32;

export const EditSpeedProfileUsocsForm = ({
  speedProfile,
  currentUsocs,
  onSuccess,
  onClose,
}: EditSpeedProfileUsocsFormProps) => {
  const [usocs, setUsocs] = useState<string[]>(currentUsocs);
  const [inputValue, setInputValue] = useState('');
  const [formError, setFormError] = useState('');

  const { mutate: updateSpeedProfile, isPending: isSaving } = useUISpeedProfilesUpdateSpeedProfile();

  const validateUsoc = (value: string): string | null => {
    if (!value.trim()) {
      return 'USOC code cannot be empty';
    }
    if (value.length > MAX_USOC_LENGTH) {
      return `USOC code must be ${MAX_USOC_LENGTH} characters or less`;
    }
    if (!USOC_PATTERN.test(value)) {
      return 'USOC code must contain only alphanumeric characters';
    }
    if (usocs.includes(value.toUpperCase())) {
      return `USOC "${value}" is already added`;
    }
    return null;
  };

  const handleChange = (_event: SyntheticEvent, newValue: string[]) => {
    // Validate and normalize new values
    const validatedUsocs: string[] = [];
    for (const value of newValue) {
      const normalized = value.toUpperCase().trim();
      if (normalized && USOC_PATTERN.test(normalized) && normalized.length <= MAX_USOC_LENGTH) {
        if (!validatedUsocs.includes(normalized)) {
          validatedUsocs.push(normalized);
        }
      }
    }
    setUsocs(validatedUsocs);
    setFormError('');
  };

  const handleInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    // Only allow alphanumeric characters in input
    const sanitized = newInputValue.replace(/[^a-zA-Z0-9]/g, '');
    setInputValue(sanitized);
  };

  const addUsoc = (value: string) => {
    const normalized = value.toUpperCase().trim();
    if (!normalized) {
      return false;
    }
    const error = validateUsoc(normalized);
    if (error) {
      setFormError(error);
      return false;
    }
    setUsocs([...usocs, normalized]);
    setInputValue('');
    setFormError('');
    return true;
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Add tag on Enter, comma, or space
    if ((event.key === 'Enter' || event.key === ',' || event.key === ' ') && inputValue) {
      event.preventDefault();
      addUsoc(inputValue);
    }
  };

  const handleBlur = () => {
    // Add any remaining input when field loses focus
    if (inputValue) {
      addUsoc(inputValue);
    }
  };

  const handleSave = () => {
    setFormError('');
    updateSpeedProfile(
      {
        groupname: speedProfile,
        data: {
          usocs: usocs,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: (error) => {
          if (error.response?.data?.detail) {
            setFormError(error.response.data.detail);
          } else {
            setFormError('Failed to update USOC codes. Please try again.');
          }
        },
      },
    );
  };

  const hasChanges =
    usocs.length !== currentUsocs.length ||
    usocs.some((u) => !currentUsocs.includes(u)) ||
    currentUsocs.some((u) => !usocs.includes(u));

  return (
    <Box sx={{ width: 600, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" p={3} pb={2}>
        <Typography variant="h5" fontWeight="bold">
          {speedProfile}
        </Typography>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, px: 3, pb: 2 }}>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" mb={1}>
          Add or remove USOC codes for this speed profile. Press Enter, comma, or space to add a new code.
        </Typography>

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={usocs}
          inputValue={inputValue}
          onChange={handleChange}
          onInputChange={handleInputChange}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const { key, ...itemProps } = getItemProps({ index });
              return <Chip key={key} label={option} size="small" {...itemProps} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="USOC Codes"
              placeholder={
                usocs.length === 0 ? 'Type USOC codes (Enter, comma, or space to add)' : 'Add more...'
              }
              helperText="Alphanumeric only, max 32 characters per code"
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  maxLength: MAX_USOC_LENGTH,
                },
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" color="text.secondary">
          {usocs.length} USOC code{usocs.length !== 1 ? 's' : ''} assigned
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        p={3}
        pt={2}
        borderTop={1}
        borderColor="divider"
      >
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving || !hasChanges}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Stack>
    </Box>
  );
};
